/* RESPONSIBLE TEAM: team-ai-agent */
import type AppService from 'embercom/services/app-service';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import IntercomRoute from 'embercom/routes/base/intercom-route';
import { ReactRoute } from 'embercom/lib/react-route';
import type RouterService from '@ember/routing/router';
import type Store from '@ember-data/store';
import { hash } from 'rsvp';
import type KnowledgeHubService from 'embercom/services/knowledge-hub-service';
import { action } from '@ember/object';
import type Guideline from 'embercom/models/ai-agent/guidelines';
import type IntercomConfirmService from 'embercom/services/intercom-confirm-service';
import type ReportingMetrics from 'embercom/services/reporting-metrics';
import { taskFor } from 'ember-concurrency-ts';
import type ReportingUnderlyingDataService from 'embercom/services/reporting-underlying-data-service';

class AutomationFinAIAgentGuidanceRoute extends IntercomRoute {
  @service declare intl: IntlService;
  @service declare appService: AppService;
  @service declare router: RouterService;
  @service declare store: Store;
  @service declare knowledgeHubService: KnowledgeHubService;
  @service declare intercomConfirmService: IntercomConfirmService;
  @service declare reportingMetrics: ReportingMetrics;
  @service declare reportingUnderlyingDataService: ReportingUnderlyingDataService;

  reactEnabled() {
    return false;
  }

  queryParams = {
    tab: {
      refreshModel: false,
    },
    aiContentSegmentIds: {
      refreshModel: false,
    },
  };

  analytics = {
    section: 'automation',
    place: 'fin-ai-agent.guidance',
  };

  get titleToken(): string {
    return this.intl.t('ai-agent.sub-menu.guidance.title');
  }

  beforeModel() {
    if (this.appService.app.canUseStandalone) {
      this.router.transitionTo('apps.app.standalone.guidance');
      return;
    }
  }

  get unsavedGuidelines() {
    let guidelines: Guideline[] = this.store.peekAll('ai-agent/guidelines').toArray();
    return guidelines.filter((guideline) => guideline.hasDirtyAttributes || guideline.isNew);
  }

  get unsavedGuidelinesWithPrompts() {
    return this.unsavedGuidelines.filter((guideline) => guideline.hasPrompt);
  }

  get emptySavedGuidelines() {
    let guidelines: Guideline[] = this.store.peekAll('ai-agent/guidelines').toArray();
    return guidelines.filter(
      (guideline) => !guideline.isNew && !guideline.hasDirtyAttributes && !guideline.hasPrompt,
    );
  }

  async model() {
    let promises = [
      this.store.findAll('ai-agent/guidelines'),
      this.store.findAll('conversation-attributes/descriptor'),
      taskFor(this.reportingMetrics.setup).perform(),
      taskFor(this.reportingUnderlyingDataService.loadAttributeMappings).perform(
        'conversation.fin.guidelines_applied.guideline_ids',
        'conversation',
        true,
      ),
      this.knowledgeHubService.fetchKnowledgeUsageSummary(),
      this.store.findAll('ai-agent/escalation-rule'),
    ];

    if (this.appService.app.canUseFeature('fin-escalation-rules')) {
      promises.push(
        taskFor(this.reportingUnderlyingDataService.loadAttributeMappings).perform(
          'conversation.fin.escalation_rules_applied.escalation_rule_ids',
          'conversation',
          true,
        ),
      );
    }

    let [guidelines] = await Promise.all(promises);

    return hash({ guidelines });
  }

  @action
  async willTransition(transition: any) {
    if (this.unsavedGuidelinesWithPrompts.length > 0) {
      transition.abort();

      let confirmOptions = {
        title: this.intl.t('operator.fin.setup.personality.unsaved-changes-modal.title'),
        confirmButtonText: this.intl.t(
          'operator.fin.setup.personality.unsaved-changes-modal.confirm',
        ),
        cancelButtonText: this.intl.t(
          'operator.fin.setup.personality.unsaved-changes-modal.cancel',
        ),
        body: this.intl.t('operator.fin.setup.personality.unsaved-changes-modal.body'),
      };
      let isConfirmed = await this.intercomConfirmService.confirm(confirmOptions);
      if (!isConfirmed) {
        return;
      }
      this.unsavedGuidelines.forEach((guideline) => guideline.rollbackAttributes());

      await Promise.all(this.emptySavedGuidelines.map((guideline) => guideline.destroyRecord()));
      transition.retry();
    } else {
      if (this.unsavedGuidelines.length > 0) {
        this.unsavedGuidelines.forEach((guideline) => guideline.rollbackAttributes());
      }
      if (this.emptySavedGuidelines.length > 0) {
        await Promise.all(this.emptySavedGuidelines.map((guideline) => guideline.destroyRecord()));
      }
    }
  }
}

export default ReactRoute(AutomationFinAIAgentGuidanceRoute);
